import { CheckCircle, Award, Users, Shield, Truck, Headphones } from 'lucide-react';
import { motion } from 'framer-motion';

const WhyChooseUs = () => {
  const benefits = [
    {
      icon: Award,
      title: 'Unique Design Identity',
      description: 'Blending cultural heritage with contemporary fashion for distinctive style',
      color: 'purple'
    },
    {
      icon: Shield,
      title: 'Quality Fabrics & Craftsmanship',
      description: 'Premium materials and exceptional attention to detail in every piece',
      color: 'pink'
    },
    {
      icon: Users,
      title: 'Customer-Centric Approach',
      description: 'Personalised styling options and dedicated service for every client',
      color: 'purple'
    },
    {
      icon: Truck,
      title: 'Global Delivery',
      description: 'Worldwide shipping with careful packaging and timely delivery',
      color: 'pink'
    },
    {
      icon: Headphones,
      title: '24/7 Support',
      description: 'Round-the-clock assistance for all your styling and order needs',
      color: 'purple'
    },
    {
      icon: CheckCircle,
      title: 'Satisfaction Guaranteed',
      description: 'Quality assurance and customer satisfaction at the heart of everything we do',
      color: 'pink'
    }
  ];

  return (
    <section id="why-choose-us" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Why Choose Us
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-brand-rose to-brand-pink mx-auto mb-6 rounded-full"></div>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto font-medium leading-relaxed">
            Discover what makes Closetbytejaswi the preferred choice for discerning fashion enthusiasts
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mb-20 md:mb-28"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-brand-rose/5 hover:shadow-2xl hover:shadow-brand-rose/10 transition-all duration-500 transform hover:-translate-y-2 group border border-brand-pink/10"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-8 bg-brand-cream group-hover:bg-brand-rose transition-colors duration-500">
                  <Icon className="w-8 h-8 md:w-10 md:h-10 text-brand-rose group-hover:text-white transition-colors duration-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose transition-colors duration-300">{benefit.title}</h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">{benefit.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-brand-cream/50 rounded-3xl p-10 md:p-16 shadow-xl shadow-brand-rose/5 border border-brand-pink/20 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-pink/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="grid md:grid-cols-4 gap-12 text-center relative z-10">
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-brand-rose mb-2 group-hover:scale-110 transition-transform duration-300">500+</div>
              <div className="text-gray-600 font-bold uppercase tracking-widest text-xs md:text-sm">Happy Customers</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-brand-rose mb-2 group-hover:scale-110 transition-transform duration-300">100%</div>
              <div className="text-gray-600 font-bold uppercase tracking-widest text-xs md:text-sm">Quality Assurance</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-brand-rose mb-2 group-hover:scale-110 transition-transform duration-300">50+</div>
              <div className="text-gray-600 font-bold uppercase tracking-widest text-xs md:text-sm">Unique Designs</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-brand-rose mb-2 group-hover:scale-110 transition-transform duration-300">24/7</div>
              <div className="text-gray-600 font-bold uppercase tracking-widest text-xs md:text-sm">Styling Support</div>
            </div>
          </div>
        </motion.div>

        {/* Mission Statement */}
        <div className="mt-20 md:mt-32 text-center relative">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-8 tracking-tight">
              Our Mission
            </h3>
            <p className="text-base md:text-xl text-gray-600 leading-relaxed mb-12 font-medium">
              To empower individuals through fashion — helping them feel confident, authentic, and stylish in every setting.
              We believe that great clothing should not only look beautiful, but also inspire confidence and self-expression.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <a href="#collections" className="bg-gradient-to-r from-brand-rose to-brand-dusty text-white px-10 py-4 rounded-full font-bold hover:from-brand-dusty hover:to-brand-rose transition-all duration-500 shadow-xl shadow-brand-rose/30 transform hover:scale-105 active:scale-95 text-center block sm:inline-block">
                Explore Our Collection
              </a>
              <a
                href="https://wa.me/447721540241"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-brand-rose border border-brand-pink/30 px-10 py-4 rounded-full font-bold hover:bg-brand-cream transition-all duration-300 shadow-lg transform hover:scale-105 active:scale-95"
              >
                Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
